// WP-C1.8 (SR-2): tag queries/mutations for parties/projects/quotations — a generic
// (entityType, entityId) -> tagId[] association, mirroring notes.ts's own hook shapes (SR-4).
import { entityTagListResponseSchema } from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const ENTITY_TAGS_KEY = ['entityTags'] as const;

export function useEntityTags(entityType: string, entityId: string): UseQueryResult<string[]> {
  return useQuery({
    queryKey: [...ENTITY_TAGS_KEY, entityType, entityId],
    queryFn: async () => {
      const params = new URLSearchParams({ entityType, entityId });
      const body = entityTagListResponseSchema.parse(
        await api.get(`/api/v1/entity-tags?${params.toString()}`),
      );
      return body.tagIds;
    },
  });
}

/** Replaces the WHOLE tag set in one call — matches TagInput's own controlled
 * value/onChange contract, so callers just pass the new full array on every change, no
 * diffing needed. */
export function useSetEntityTags(
  entityType: string,
  entityId: string,
): UseMutationResult<void, Error, string[]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tagIds: string[]) => {
      await api.put('/api/v1/entity-tags', { entityType, entityId, tagIds });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [...ENTITY_TAGS_KEY, entityType, entityId],
      });
    },
  });
}
